import React, { useEffect, useState, useCallback } from "react";
import styles from "./index.module.css";
import { useSelector } from "react-redux";
import { IAppState } from "../../../../store";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import { Search, Clear, FilterList } from "@material-ui/icons";
import { IInvoice, InvoiceStatus } from "../../../../reducers/bookings/types";
import { StyledTableCell, StyledTableRow } from "../../../StyledTable";
import { useLazyQuery } from "@apollo/client";
import { GET_INVOICES } from "../../../../graphql/invoices/getInvoices";
import { useHistory, useLocation } from "react-router-dom";
import { DATE_TYPE, toCurrency } from "../../../utils";
import { getLocalizedBookingSyntex, getLocalizedDateFormat } from "../../../../utils/localized.syntex";
import { useTranslation } from "react-i18next";
import { withStyles } from "@material-ui/core/styles";

export const StyledInput = withStyles({
  root: {
    "& .MuiOutlinedInput-input": {
      padding: "8px 10px 9px 0 !important",
      borderColor: "#e9e9e9",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(0,0,0,0.1)",
    },
  },
})(TextField);

const useStyles = makeStyles((theme) => ({
  chip: {
    fontSize: "12px",
    height: "24px",
    borderRadius: "12px",
    fontWeight: 500
  },
  filterCard: {
    padding: "16px",
    marginBottom: "16px",
    backgroundColor: "#f5f5f5"
  },
  tabs: {
    marginBottom: "16px",
    backgroundColor: "#fff",
    borderRadius: "4px"
  },
  statusChip: {
    fontWeight: 600,
    fontSize: "11px"
  }
}));

type LocationState = {
  applyFilters?: {
    invoiceStatuses: InvoiceStatus[];
  };
};

const Invoices = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { t } = useTranslation();
  const user = useSelector((state: IAppState) => state.authReducer.user);
  const website = useSelector(
    (state: IAppState) => state.consumerWebsiteReducer.consumerWebsite
  );
  const { currency, locale} = website.organisation;
  const { country } = website.organisation.address;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState<keyof IInvoice>("dateCreated");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [invoices, setInvoices] = useState<IInvoice[]>([]);
  const [allInvoices, setAllInvoices] = useState<IInvoice[]>([]);
  const [tempStatuses, setTempStatuses] = useState<string[]>([]);

  useEffect(() => {
    if (filterModalOpen) {
      setTempStatuses(selectedStatuses);
    }
  },[filterModalOpen, selectedStatuses])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput);
        setPage(0);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const [getInvoices, { loading, error }] = useLazyQuery(
    GET_INVOICES,
    {
      fetchPolicy: "cache-and-network",
      onCompleted: (data) => {
        if (data?.consumerInvoices) {
          setAllInvoices(data.consumerInvoices);
        }
      }
    }
  );

  useEffect(() => {
    if (user) {
      getInvoices({
        variables: {
          businessCustomerId: user.businesscustomers?.id || undefined,
          customerId: user.customerId || undefined,
        },
      });
    }
  }, [user]);

  useEffect(() => {
    if (location.state?.applyFilters?.invoiceStatuses?.length) {
      setSelectedStatuses(location.state.applyFilters.invoiceStatuses);
    }
  }, [location.state]);

  useEffect(() => {
    let filtered = [...allInvoices];

    if (selectedStatuses.length > 0) {
      filtered = filtered.filter((invoice) =>
        selectedStatuses.includes(invoice.status)
      );
    }

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((invoice) =>
        invoice.invoiceRef.toLowerCase().includes(searchLower) ||
        invoice.booking?.referenceNumber?.toLowerCase().includes(searchLower)
      );
    }

    filtered.sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];
      if (!aValue) return 1;
      if (!bValue) return -1;
      
      const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return sortOrder === "asc" ? comparison : -comparison;
    });

    const startIndex = page * rowsPerPage;
    const paginatedInvoices = filtered.slice(startIndex, startIndex + rowsPerPage);
    
    setInvoices(paginatedInvoices);
  }, [allInvoices, searchTerm, selectedStatuses, sortBy, sortOrder, page, rowsPerPage]);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  }, []);

  const handleStatusFilterChange = useCallback((event: any) => {
    const value = event.target.value as string[];
    setTempStatuses(value);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setSelectedStatuses(tempStatuses);
    setPage(0);
    setFilterModalOpen(false);
  }, [tempStatuses]);

  const handleResetFilters = useCallback(() => {
    setSelectedStatuses([]);
    setTempStatuses([]);
    setPage(0);
  }, []);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleRequestSort = useCallback((property: keyof IInvoice) => {
    const isAsc = sortBy === property && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortBy(property);
  }, [sortBy, sortOrder]);

  const handleClearFilters = useCallback(() => {
    setSearchInput("");
    setSearchTerm("");
    setSelectedStatuses([]);
    setTempStatuses([]);
    setPage(0);
  }, []);

  const getTotalCount = () => {
    let filtered = [...allInvoices];
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter((invoice) =>
        selectedStatuses.includes(invoice.status)
      );
    }
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((invoice) =>
        invoice.invoiceRef.toLowerCase().includes(searchLower) ||
        invoice.booking?.referenceNumber?.toLowerCase().includes(searchLower)
      );
    }
    return filtered.length;
  };

  const totalCount = getTotalCount();

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">Error loading invoices: {error.message}</Typography>
      </Box>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: "24px" }}>
        <Typography variant="h3" style={{ fontWeight: 600, fontSize: "2rem" }}>
          {t("root:invoices")}
        </Typography>

        <Box display="flex" alignItems="center" style={{ gap: "16px" }}>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setFilterModalOpen(true)}
            style={{
              height: "40px",
              textTransform: "none",
              borderColor: selectedStatuses.length > 0 ? "#1976d2" : "rgba(0, 0, 0, 0.23)",
              color: selectedStatuses.length > 0 ? "#1976d2" : "inherit"
            }}
          >
            Filters {selectedStatuses.length > 0 && `(${selectedStatuses.length})`}
          </Button>

          {selectedStatuses.length > 0 && (
            <Button 
              variant="outlined" 
              startIcon={<Clear />} 
              onClick={handleClearFilters}
              style={{ 
                whiteSpace: "nowrap",
                height: "40px",
                textTransform: "none",
                borderColor: "rgba(0, 0, 0, 0.23)"
              }}
            >
              Clear All
            </Button>
          )}
        </Box>
      </Box>

      <Dialog
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" style={{ fontWeight: 600 }}>FILTERS</Typography>
            <Box>
              <Button
                color="primary"
                onClick={handleResetFilters}
                style={{ textTransform: "none", marginRight: 8 }}
              >
                RESET
              </Button>
              <IconButton size="small" onClick={() => setFilterModalOpen(false)}>
                <Clear />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  multiple
                  value={tempStatuses}
                  onChange={handleStatusFilterChange}
                  MenuProps={{
                    getContentAnchorEl: null
                  }}
                  input={<OutlinedInput label="Status" />}
                  renderValue={(selected) => 
                    (selected as string[]).length === 0 ? "All" : (selected as string[])
                      .map((s) => t(`root:currentInvoiceStatus.${s}` as any))
                      .join(", ")
                  }
                >
                  <MenuItem value={InvoiceStatus.UNPAID}>
                    <Checkbox checked={tempStatuses.includes(InvoiceStatus.UNPAID)} />
                    <ListItemText primary={t("root:unpaid")} />
                  </MenuItem>
                  <MenuItem value={InvoiceStatus.PARTIALLY_PAID}>
                    <Checkbox checked={tempStatuses.includes(InvoiceStatus.PARTIALLY_PAID)} />
                    <ListItemText primary="Partially Paid" />
                  </MenuItem>
                  <MenuItem value={InvoiceStatus.PAID}>
                    <Checkbox checked={tempStatuses.includes(InvoiceStatus.PAID)} />
                    <ListItemText primary={t("root:paid")} />
                  </MenuItem>
                  <MenuItem value={InvoiceStatus.VOID}>
                    <Checkbox checked={tempStatuses.includes(InvoiceStatus.VOID)} />
                    <ListItemText primary={t("root:void")} />
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
          onClick={handleApplyFilters} color="primary" variant="contained"
          disabled={tempStatuses.length === 0}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      <Card className={classes.filterCard}>
        <Grid container spacing={2} alignItems="flex-start">
          {selectedStatuses.length > 0 && (
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: "8px" }}>
                <Typography variant="body2" style={{ fontWeight: 600, marginRight: 8 }}>
                  Active Filters:
                </Typography>
                {selectedStatuses.map((status) => (
                  <Chip
                    key={status}
                    label={t(`root:currentInvoiceStatus.${status}` as any)}
                    onDelete={() => {
                      setSelectedStatuses(selectedStatuses.filter(s => s !== status));
                      setPage(0);
                    }}
                    color="primary"
                    size="small"
                    className={classes.chip}
                  />
                ))}
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              placeholder={`${t("root:searchInvoiceId")} or ${t("root:bookingRef")}`}
              value={searchInput}
              onChange={handleSearchChange}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchInput("");
                        setSearchTerm("");
                      }}
                      edge="end"
                    >
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>
      </Card>

      <Box style={{ position: "relative" }}>
        {loading && (
          <Box
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255,255,255,0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10
            }}
          >
            <CircularProgress />
          </Box>
        )}
        
        <TableContainer style={{ maxHeight: "600px" }}>
          <Table stickyHeader aria-label="invoices table">
            <TableHead>
              <TableRow>
                <StyledTableCell>
                  <TableSortLabel
                    active={sortBy === "invoiceRef"}
                    direction={sortBy === "invoiceRef" ? sortOrder : "asc"}
                    onClick={() => handleRequestSort("invoiceRef")}
                  >
                    {t("root:invoiceId")}
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell>
                  <TableSortLabel
                    active={sortBy === "status"}
                    direction={sortBy === "status" ? sortOrder : "asc"}
                    onClick={() => handleRequestSort("status")}
                  >
                    {t("root:status")}
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell>{`${getLocalizedBookingSyntex(country)} ${t("root:ref")}`}</StyledTableCell>
                <StyledTableCell>
                  <TableSortLabel
                    active={sortBy === "dateCreated"}
                    direction={sortBy === "dateCreated" ? sortOrder : "asc"}
                    onClick={() => handleRequestSort("dateCreated")}
                  >
                    {t("root:invoiceDate")}
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell>
                  <TableSortLabel
                    active={sortBy === "totalPayableAmount"}
                    direction={sortBy === "totalPayableAmount" ? sortOrder : "asc"}
                    onClick={() => handleRequestSort("totalPayableAmount")}
                  >
                    {t("root:total")}
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell>
                  <TableSortLabel
                    active={sortBy === "dueAmount"}
                    direction={sortBy === "dueAmount" ? sortOrder : "asc"}
                    onClick={() => handleRequestSort("dueAmount")}
                  >
                    {t("root:balanceDue")}
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell>
                  <TableSortLabel
                    active={sortBy === "dueDate"}
                    direction={sortBy === "dueDate" ? sortOrder : "asc"}
                    onClick={() => handleRequestSort("dueDate")}
                  >
                    {t("root:dueDate")}
                  </TableSortLabel>
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.length > 0 ? (
                invoices.map((invoice, index) => (
                  <StyledTableRow
                    key={invoice.id || index}
                    onClick={() => history.push(`/account/invoices/${invoice.id}`)}
                    style={{ cursor: "pointer" }}
                  >
                    <StyledTableCell>{invoice.invoiceRef}</StyledTableCell>
                    <StyledTableCell>
                      <span
                        className={classes.statusChip}
                        style={{
                          padding: "4px 8px",
                          borderRadius: "12px",
                          backgroundColor: ["PARTIALLY PAID", "UNPAID"].includes(invoice.status)
                            ? "#ffebee"
                            : invoice.status === InvoiceStatus.VOID
                              ? "#f5f5f5"
                              : "#e8f5e9",
                          color: ["PARTIALLY PAID", "UNPAID"].includes(invoice.status)
                            ? "#c62828"
                            : invoice.status === InvoiceStatus.VOID
                              ? "#757575"
                              : "#2e7d32"
                        }}
                      >
                        {["PARTIALLY PAID", "UNPAID"].includes(invoice.status)
                          ? t("root:unpaid").toUpperCase()
                          : t(`root:currentInvoiceStatus.${invoice.status}`)}
                      </span>
                    </StyledTableCell>
                    <StyledTableCell>
                      {invoice.booking?.referenceNumber || t("root:na")}
                    </StyledTableCell>
                    <StyledTableCell>
                      {getLocalizedDateFormat(
                        country,
                        invoice.dateCreated,
                        DATE_TYPE.EXPANDED,
                        invoice.booking?.branch?.timeZone
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      {toCurrency(invoice.totalPayableAmount, currency, locale)}
                    </StyledTableCell>
                    <StyledTableCell>
                      {toCurrency(invoice.dueAmount, currency, locale)}
                    </StyledTableCell>
                    <StyledTableCell>
                      {invoice.dueDate
                        ? getLocalizedDateFormat(
                            country,
                            invoice.dueDate,
                            DATE_TYPE.EXPANDED,
                            invoice.booking?.branch?.timeZone
                          )
                        : t("root:na")}
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <TableRow>
                  <StyledTableCell colSpan={7} align="center">
                    <Typography style={{ opacity: 0.5, padding: "40px 0" }}>
                      {t("root:noRecordFound")}
                    </Typography>
                  </StyledTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onChangePage={handleChangePage}
          rowsPerPage={rowsPerPage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 100]}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                style: {
                  marginTop: "auto"
                },
              },
            },
          }}
        />
      </Box>
    </div>
  );
};

export default Invoices;
